import SearchIcon from "@redotech/redo-icons/search-md.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import { Placeholder } from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import { memo, useCallback, useEffect, useRef } from "react";
import { RedoBadge } from "../../../arbiter-components/badge/redo-badge";
import { KeyboardShortcut } from "../../../components/ui/keyboard-shortcut";
import { useCommandPalette } from "../../contexts/command-palette-context";
import * as styles from "./command-palette-search-input.module.css";

export interface CommandPaletteSearchInputProps {
  value?: string;
  onChange?: (value: string) => void;
}

export const CommandPaletteSearchInput = memo(
  function CommandPaletteSearchInput({
    value = "",
    onChange,
  }: CommandPaletteSearchInputProps) {
    const { state, actions, searchInputRef } = useCommandPalette();
    const searchTimeoutRef = useRef<NodeJS.Timeout>();

    const handleUpdate = useCallback(
      ({ editor }: { editor: any }) => {
        const content = editor.getText();
        onChange?.(content);

        // Debounced search
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        if (content.trim()) {
          // Set loading state immediately when we start typing
          actions.setIsSearching(true);

          searchTimeoutRef.current = setTimeout(() => {
            void actions.performSearch(content);
          }, 300);
        } else {
          // If empty, clear results immediately
          actions.setIsSearching(false);
          void actions.performSearch("");
        }
      },
      [onChange, actions],
    );

    const editor = useEditor({
      extensions: [
        Document,
        Paragraph,
        Text,
        Placeholder.configure({ placeholder: "Search" }),
      ],
      content: value || undefined,
      onUpdate: handleUpdate,
      editorProps: {
        attributes: { class: styles.editor },
        handleKeyDown: (view, event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            if (event.shiftKey) {
              if (state.onAskAI) {
                state.onAskAI(state.searchValue);
              }
            } else if (event.metaKey || event.ctrlKey) {
              actions.openInNewTab();
            } else {
              actions.open();
            }
            return true;
          }

          if (event.key === "Backspace") {
            const currentText = view.state.doc.textContent;
            const activeFilters = state.filters.filter(
              (filter) => filter.enabled,
            );

            // If text is empty and there are active filters, remove the last one
            if (currentText.trim() === "" && activeFilters.length > 0) {
              event.preventDefault();
              const lastFilter = activeFilters[activeFilters.length - 1];
              actions.toggleFilter(lastFilter.id);
              return true;
            }
          }

          return false;
        },
      },
      autofocus: true,
    });

    useEffect(() => {
      if (!editor) return;

      const placeholder = "Search";

      for (const extension of editor.extensionManager.extensions) {
        if (extension.name === "placeholder") {
          extension.options.placeholder = placeholder;
        }
      }

      // Update editor content
      const targetContent = value || "";
      const currentContent = editor.getText();

      if (currentContent !== targetContent) {
        editor.commands.setContent(targetContent);
      }

      editor.view.dispatch(editor.state.tr);
    }, [editor, value]);

    // Assign editor to ref for external focus control
    useEffect(() => {
      if (searchInputRef && editor) {
        (searchInputRef as any).current = {
          focus: () => editor.commands.focus(),
        };
      }
    }, [editor, searchInputRef]);

    const handleContainerClick = useCallback(() => {
      editor?.commands.focus();
    }, [editor]);

    const handleAskAIClick = useCallback(() => {
      if (state.onAskAI) {
        state.onAskAI(state.searchValue);
      }
    }, [state]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    return (
      <div className={styles.wrapper}>
        <div className={styles.container} onClick={handleContainerClick}>
          <div className={styles.prefix}>
            <SearchIcon className={styles.searchIcon} />
          </div>
          <div className={styles.editorWrapper}>
            {state.filters
              .filter((filter) => filter.enabled)
              .map((filter) => (
                <button
                  className={styles.activeFilterButton}
                  key={filter.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    actions.toggleFilter(filter.id);
                  }}
                  type="button"
                >
                  <RedoBadge
                    color="gray"
                    iconTrailing={{ type: "icon", Icon: XCloseIcon }}
                    size="sm"
                    text={filter.label}
                  />
                </button>
              ))}
            <EditorContent editor={editor} />
          </div>
          {state.onAskAI && (
            <div className={styles.suffixContent}>
              <span className="text-muted-foreground text-xs">Ask AI</span>
              <button onClick={handleAskAIClick} type="button">
                <KeyboardShortcut
                  className="text-xs [&>kbd]:text-xs"
                  keys={["Shift ↩"]}
                />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
);
